import * as am4charts from '@amcharts/amcharts4/charts';
// CORE
import * as am4core from '@amcharts/amcharts4/core';
import { useTheme } from '@mui/material/styles';
import useFormatAmount from 'app-components/hooks/privacyMode/useFormatAmount';
import selectPrivacyModeEnabled from 'app-components/hooks/privacyMode/selectPrivacyModeEnabled';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useAppSelector } from 'hooks/redux';

function SeriesBarChart(props) {
  const theme = useTheme();
  const { chartData, average, isIncome, colorOverride } = props;

  const privacyModeEnabled = useAppSelector(selectPrivacyModeEnabled);
  const formatAmount = useFormatAmount();

  const mainColor = colorOverride
    ? am4core.color(colorOverride)
    : am4core.color(isIncome ? theme.palette.legend.income : theme.palette.legend.bill);
  const fadedColor = colorOverride
    ? am4core.color(theme.applyOpacityToHex(colorOverride, 0.3))
    : // eslint-disable-next-line no-nested-ternary
      am4core.color(
        isIncome
          ? theme.applyOpacityToHex(theme.palette.legendDim.income, 0.5)
          : theme.applyOpacityToHex(theme.palette.legendDim.bill, 0.5),
      );
  const dark = am4core.color(theme.palette.text.secondary);
  const rangeColor = theme.palette.chart.averageLine;
  const white = theme.palette.background.paper;

  let chart = {};

  // radius adapters
  const cornerRadiusBottomAdapter = (radius, target) => {
    if (!target.dataItem) {
      return radius;
    }
    const isNegative = target.dataItem.dataContext.totalAmount < 0;
    return isNegative ? 4 : 0;
  };
  const cornerRadiusTopAdapter = (radius, target) => {
    if (!target.dataItem) {
      return radius;
    }
    const isNegative = target.dataItem.dataContext.totalAmount < 0;
    return isNegative ? 0 : 4;
  };
  // label adapters
  const labelColorAdapter = (currentColor, target) => {
    if (!target.dataItem) {
      return currentColor;
    }
    return target.dataItem.column.maxHeight < 20 ? dark : white;
  };
  const labelPositionAdapter = (dy, target) => {
    if (!target.dataItem) {
      return dy;
    }
    if (target.dataItem.dataContext.totalAmount === 0) {
      return average < 0 ? 12 : -12;
    }
    if (target.dataItem.column.pixelHeight > 0 && target.dataItem.column.pixelHeight < 20) {
      return -target.dataItem.column.pixelHeight - 6;
    }
    return target.dataItem.dataContext.totalAmount < 0 ? 12 : -12;
  };

  const setUpChart = () => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    chart = am4core.create('Series-Bar-Chart', am4charts.XYChart);
    chart.paddingLeft = 6;
    chart.paddingTop = 30;
    chart.paddingRight = 24;
    chart.paddingBottom = 0;
    chart.zoomOutButton.disabled = true; // Sometimes the button can appear when the chart is first loading. This hides it.
    chart.maxZoomLevel = 1;

    chart.numberFormatter.numberFormat = "'$'#,###.";
    chart.dateFormatter.dateFormat = 'MM';

    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = 'month';
    categoryAxis.tooltip.disabled = true;
    categoryAxis.renderer.grid.template.disabled = true;
    categoryAxis.renderer.ticks.template.disabled = false;
    categoryAxis.renderer.labels.template.fontSize = 12;
    categoryAxis.renderer.labels.template.fill = dark;
    categoryAxis.renderer.labels.template.opacity = 0.6;
    categoryAxis.renderer.minGridDistance = 30;
    categoryAxis.paddingRight = 54;

    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.renderer.baseGrid.disabled = true;
    valueAxis.renderer.grid.template.disabled = true;
    valueAxis.renderer.labels.template.disabled = true;
    valueAxis.width = 72;

    // make sure the minimum is at least 0
    let min = 0;
    chartData.forEach((datum) => {
      if (datum?.totalAmount < min) {
        min = datum?.totalAmount;
      }
    });
    valueAxis.min = min;

    // columns series
    // --------------------------
    // faded bar for current month prediction
    const series1 = chart.series.push(new am4charts.ColumnSeries());
    series1.name = 'Predicted';
    series1.dataFields.valueY = 'totalAmount';
    series1.dataFields.categoryX = 'month';
    series1.sequencedInterpolation = true;
    series1.clustered = false;

    // posted / historical bars
    const series2 = chart.series.push(new am4charts.ColumnSeries());
    series2.showOnInit = false;
    series2.dataFields.valueY = 'currentAmount';
    series2.dataFields.categoryX = 'month';
    series2.sequencedInterpolation = true;
    series2.clustered = false;

    const columnTemplate1 = series1.columns.template;
    columnTemplate1.fill = fadedColor;
    columnTemplate1.strokeWidth = 0;
    columnTemplate1.column.cornerRadius(4, 4, 0, 0);
    columnTemplate1.width = 54;
    columnTemplate1.zIndex = 2;

    const columnTemplate2 = series2.columns.template;
    columnTemplate2.fill = mainColor;
    columnTemplate2.strokeWidth = 0;
    columnTemplate2.column.cornerRadius(4, 4, 0, 0);
    columnTemplate2.width = 54;
    columnTemplate2.zIndex = 2;

    // amount labels on bar
    const bullets = series2.bullets.push(new am4charts.LabelBullet());
    bullets.label.text = `${privacyModeEnabled ? '***' : '{totalAmount}'}`;
    bullets.label.fill = white;
    bullets.label.fontSize = 12;
    bullets.locationY = 1;
    bullets.dy = 0;

    // Visible line for x-axis
    const axisRange = valueAxis.axisRanges.create();
    axisRange.value = 0;
    axisRange.grid.strokeOpacity = 1;
    axisRange.grid.fill = am4core.color(fadedColor);
    axisRange.grid.stroke = am4core.color(fadedColor);

    // Average line as an axis range
    const range = valueAxis.axisRanges.create();
    range.value = average;
    range.grid.fill = am4core.color(rangeColor);
    range.grid.stroke = am4core.color(rangeColor);
    range.grid.strokeWidth = 2;
    range.grid.strokeOpacity = 0.7;

    // label for the average line
    range.label.fontSize = 14;
    range.label.fill = am4core.color(dark);
    range.label.animate([], 0);
    range.label.dy = -11;
    range.label.html = `<div style="height: 42px;">
        Average \n
        <div style="display: flex; align-items: center;">
          <div style="
              height: 20px;
              background-color: ${rangeColor};
              color: ${theme.palette.common.white};
              padding: 2px 12px;
              line-height: 16px;
              width: fit-content;
              border-radius: 4px;
              min-width: 54px;
              text-align: center;
            "
          >${formatAmount(average, undefined, '0,0')}</div>
          <div style="height: 18px; margin-left: -1px; border: 9px solid transparent; border-left-color: ${rangeColor};"/>
        </div>
      </div>`;
    range.grid.above = true;

    chart.data = chartData;

    // adapters
    columnTemplate1.column.adapter.add('cornerRadiusBottomLeft', cornerRadiusBottomAdapter);
    columnTemplate1.column.adapter.add('cornerRadiusBottomRight', cornerRadiusBottomAdapter);
    columnTemplate1.column.adapter.add('cornerRadiusTopLeft', cornerRadiusTopAdapter);
    columnTemplate1.column.adapter.add('cornerRadiusTopRight', cornerRadiusTopAdapter);

    columnTemplate2.column.adapter.add('cornerRadiusBottomLeft', cornerRadiusBottomAdapter);
    columnTemplate2.column.adapter.add('cornerRadiusBottomRight', cornerRadiusBottomAdapter);
    columnTemplate2.column.adapter.add('cornerRadiusTopLeft', cornerRadiusTopAdapter);
    columnTemplate2.column.adapter.add('cornerRadiusTopRight', cornerRadiusTopAdapter);

    bullets.label.adapter.add('dy', labelPositionAdapter);
    bullets.label.adapter.add('fill', labelColorAdapter);

    return () => {
      chart.dispose();
    };
  };

  // init
  useEffect(setUpChart, [average, isIncome, theme, privacyModeEnabled]);

  // updateChart
  useEffect(() => {
    chart.data = chartData;
  }, [chartData]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div id='Series-Bar-Chart' style={{ width: '100%', height: '100%', overflow: 'hidden' }} />;
}

SeriesBarChart.propTypes = {
  chartData: PropTypes.array,
  average: PropTypes.number,
  isIncome: PropTypes.bool,
  colorOverride: PropTypes.string,
};

export default SeriesBarChart;
